/**
 * PWA install helper.
 *
 * Captures the Chromium `beforeinstallprompt` event so we can trigger the
 * native "Install app" prompt on demand (desktop Edge / Chrome — the target).
 * Falls back to manual instructions when the native prompt isn't available
 * (e.g. the event hasn't fired yet, or non-Chromium browsers). Hides itself
 * once the app is running as an installed standalone window.
 */
import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const detectInstalled = (): boolean => {
  if (typeof window === 'undefined') return false;
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  // iOS Safari exposes navigator.standalone
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone || iosStandalone;
};

export interface PwaInstall {
  /** Whether the install control should be shown at all (not already installed). */
  showInstall: boolean;
  /** Whether a native install prompt is ready (one-click install possible). */
  canPrompt: boolean;
  /** Trigger the native install prompt. Returns true if the user accepted. */
  promptInstall: () => Promise<boolean>;
}

export function usePwaInstall(): PwaInstall {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState<boolean>(detectInstalled);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferred) return false;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice.outcome === 'accepted';
  }, [deferred]);

  return {
    showInstall: !isInstalled,
    canPrompt: !!deferred && !isInstalled,
    promptInstall,
  };
}

export default usePwaInstall;
